import React, { useCallback, useContext, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { AuthApiService } from '../../services/AuthApiService';
import { ApplicationContext, AppContext } from '../../context/Contexts';
import Logo from '../_shared/Logo';
import LanguageSelector from './LanguageSelector';
import { captureAuthError } from '../../helpers/Sentry';
import { getErrorMessageKey } from '../../helpers/RequestErrors';

const ForgotPassword: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const context = useContext(ApplicationContext);
  const translatorService = (context as AppContext).translatorService;

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const t = useCallback(
    (key: string, fallback: string) => translatorService.Tranlate(key, fallback),
    [translatorService]
  );

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await AuthApiService.forgotPassword(email);
      const data = response.data as any;

      if (!data.success) {
        enqueueSnackbar(t(data.message || 'FLEET_ERROR_MESSAGE', 'An error occurred'), {
          variant: 'error'
        });
        setIsLoading(false);
        return;
      }

      enqueueSnackbar(
        t(
          'FLEET_RESET_PASS_EMAIL_SENT_MESSAGE',
          'An email has been sent to your email address with instructions to reset your password'
        ),
        { variant: 'success' }
      );
      history.push('/auth/login');
    } catch (err) {
      captureAuthError('Forgot Password', err);
      enqueueSnackbar(t(getErrorMessageKey(err, 'FLEET_ERROR_MESSAGE'), 'An error occurred'), {
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      position="relative"
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      style={{ backgroundColor: '#f4f5fd' }}
    >
      <LanguageSelector />
      <Box
        style={{
          backgroundColor: '#fff',
          borderRadius: 8,
          padding: '32px',
          maxWidth: 500,
          width: '100%',
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)'
        }}
      >
        <Logo />
        <Typography
          variant="body2"
          style={{ color: '#888', marginBottom: 24, textAlign: 'center' }}
        >
          {t(
            'FLEET_FORGOT_PASSWORD_SUBTITLE',
            'Enter your email and we will send you a reset link'
          )}
        </Typography>

        <ValidatorForm onSubmit={handleSubmit} instantValidate={true}>
          <Box mb={3}>
            <TextValidator
              fullWidth
              label={t('FLEET_EMAIL_TEXT_LABEL', 'Email')}
              variant="outlined"
              type="email"
              name="email"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
              validators={['required', 'isEmail']}
              errorMessages={[
                t('VALIDATORS_REQUIRED', 'This field is required'),
                t('VALIDATORS_EMAIL', 'Please enter a valid email')
              ]}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isLoading}
            style={{ textTransform: 'none', marginBottom: 12 }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('FLEET_FORGOT_PASSWORD_BUTTON_TEXT', 'Send reset link')
            )}
          </Button>

          <Button
            color="primary"
            className="text-primary"
            fullWidth
            size="small"
            onClick={() => history.push('/auth/login')}
            style={{ textTransform: 'none' }}
          >
            {t('FLEET_BACK_TO_LOGIN_BUTTON_TEXT', 'Back to login')}
          </Button>
        </ValidatorForm>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
